import React, { useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'

import { useTheme } from 'styled-components'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MaterialIcon, Pill, Tonality } from '@dsny/dsny-component-library'
import styles from '../../Dropdowns/Dropdowns.module.scss'
import { DropdownTrigger } from './FilterDropdown.styles'

const FilterDropdown = ({
  label = 'filter',
  filters = [],
  onFilterSelect = noop,
  onCloseIconClick = noop,
  triggerStyle = {},
  footer = null,
  header = null,
  iconName = 'KeyboardArrowDown',
  iconPosition = 'tailing',
  iconStyle = {},
  iconContainerStyles = {},
  singleSelect = false
}) => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)

  const onItemSelect = (e, label, value) => {
    e.preventDefault()
    onFilterSelect?.(e, label, value)

    // Close dropdown automatically for single-select (radio button behavior)
    if (singleSelect) {
      setIsOpen(false)
    }
  }

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownTrigger style={{ justifySelf: 'center', border: 'none' }}>
        <Pill
          style={triggerStyle}
          variant="tonal"
          mode="dark"
          label={label}
          onIconClick={onCloseIconClick}
          iconPosition={iconPosition}
          iconContainerStyles={iconContainerStyles}
          icon={
            <MaterialIcon
              name={iconName}
              color={theme.colors.white0}
              style={{ fontSize: 14, ...iconStyle }}
            />
          }
        />
      </DropdownTrigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="bottom"
          align="start"
          style={{
            background: theme.colors.neutralW20,
            color: theme.colors.neutralW100,
            padding: '5px 0',
            height: 'auto',
            maxHeight: 216,
            overflow: 'auto',
            border: `0.5px solid ${theme.colors.neutralW30}`,
            zIndex: 1000
          }}
          className={styles.DropdownMenuContent}
          sideOffset={5}
          avoidCollisions
          collisionPadding={150}
        >
          {header}
          {filters.map((option, index) => {
            return (
              <DropdownMenu.Item
                key={index}
                className={styles.DropdownMenuItem}
                onSelect={(e) => onItemSelect(e, option?.label, option?.value)}
                style={{ gap: 8 }}
              >
                {singleSelect ? (
                  <Tonality.RadioButton
                    id={`radio-${option?.value}`}
                    checked={option?.isSelected}
                    className={styles.DropdownMenuItemRadioFiltering}
                    mode="dark"
                    onChange={(e) => {
                      e.stopPropagation()
                      onItemSelect(e, option?.label, option?.value)
                    }}
                  />
                ) : (
                  <Tonality.Checkbox
                    checked={option?.isSelected}
                    mode="dark"
                    style={{
                      marginRight: 3,
                      background: option?.isSelected
                        ? theme.colors.interact70
                        : theme.colors.neutralW20
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      onItemSelect(e, option?.label, option?.value)
                    }}
                  />
                )}
                {option?.label}
              </DropdownMenu.Item>
            )
          })}
          {footer}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

FilterDropdown.propTypes = {
  label: PropTypes.string,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ),
  onFilterSelect: PropTypes.func,
  onCloseIconClick: PropTypes.func,
  triggerStyle: PropTypes.object,
  footer: PropTypes.node,
  header: PropTypes.node,
  iconName: PropTypes.string,
  iconPosition: PropTypes.oneOf(['tailing', 'leading']),
  iconStyle: PropTypes.object,
  iconContainerStyles: PropTypes.object,
  singleSelect: PropTypes.bool
}

export default FilterDropdown
